import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, Users, Loader2, CheckCircle2, Shield, GraduationCap, Briefcase, Calculator } from "lucide-react";
import Footer from "../components/Footer";
import { toast } from "sonner";
import Honeypot from "../components/Honeypot";

export default function Groups() {
  const [params] = useSearchParams();
  const preselectedCity = params.get("city") || "";
  const preselectedRoute = params.get("route") || "";

  const [cities, setCities] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    group_size: 20,
    age_range: "16-18",
    cities: preselectedCity ? [preselectedCity] : [],
    preferred_route_slug: preselectedRoute || null,
    travel_window: "",
    budget_per_person_eur: 600,
    special_requirements: "",
  });
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [err, setErr] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  // Live group quote (computed server-side; debounced)
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteNights, setQuoteNights] = useState(10);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/featured-routes").then((r) => setRoutes(r.data));
  }, []);

  // Recompute quote whenever the inputs that drive it change.
  useEffect(() => {
    if (!form.group_size || form.group_size < 5) {
      setQuote(null);
      return;
    }
    const handle = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const { data } = await api.post("/group-quote", {
          group_size: Number(form.group_size),
          nights: Number(quoteNights) || 7,
          route_slug: form.preferred_route_slug || null,
          age_range: form.age_range || null,
        });
        setQuote(data);
      } catch {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [form.group_size, form.preferred_route_slug, form.age_range, quoteNights]);

  const fmtEur = (n) => (n == null ? "—" : `€${Math.round(n).toLocaleString()}`);
  const selectedRouteName = useMemo(() => {
    if (!form.preferred_route_slug) return null;
    return routes.find((r) => r.slug === form.preferred_route_slug)?.name || null;
  }, [form.preferred_route_slug, routes]);

  const toggleCity = (slug) => {
    setForm((f) => ({
      ...f,
      cities: f.cities.includes(slug) ? f.cities.filter((c) => c !== slug) : [...f.cities, slug],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (website) return; // honeypot tripped
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/group-inquiry", {
        ...form,
        budget_per_person_eur: form.budget_per_person_eur ? Number(form.budget_per_person_eur) : null,
        preferred_route_slug: form.preferred_route_slug || null,
        website,
      });
      setConfirm(data);
      toast.success("Inquiry received — our conductor will reply within one business day.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (confirm) {
    return (
      <div data-testid="groups-confirm" className="pt-32 pb-16 min-h-screen">
        <div className="max-w-2xl mx-auto px-6">
          <div className="iq-card p-12 text-center animate-fade-up">
            <CheckCircle2 className="h-12 w-12 text-gold mx-auto mb-5" />
            <h1 className="font-display text-cream text-4xl tracking-wider mb-3">Inquiry received</h1>
            <p className="font-serif italic text-cream-dim text-lg leading-relaxed mb-6">
              Reference <span className="text-gold font-display tracking-wider">#{confirm.reference}</span>.
              Our group desk will respond within one business day with a tailored quote, accommodation availability, and per-person pricing.
            </p>
            <Link to="/" className="iq-btn-primary">Return home</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="groups-page" className="pt-safe-navbar-lg pb-16">
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="mb-10 max-w-2xl">
          <div className="iq-overline mb-2"><Users className="inline h-3 w-3 mr-1" /> Group bookings</div>
          <h1 className="font-display text-cream text-5xl md:text-6xl tracking-wider">Move ten. Move five hundred.</h1>
          <p className="font-serif italic text-cream-dim text-lg mt-3 leading-relaxed">
            Schools, sports teams, choirs, scouts, university trips — one route, one invoice, one conductor.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {[
            { Icon: GraduationCap, title: "School trips", body: "Supervised minors, verified hostels, breakfast & dinner blocks." },
            { Icon: Shield, title: "Safety-rated stays", body: "Every property pre-vetted with our Safety Score (24/7 reception, female dorms, security)." },
            { Icon: Briefcase, title: "One invoice", body: "Bulk rail, accommodation, optional museum bundles. PO-friendly billing." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="iq-card p-5">
              <Icon className="h-6 w-6 text-gold mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-cream text-xl tracking-wider mb-1">{title}</h3>
              <p className="text-cream-dim text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* Live group quote calculator */}
        <div data-testid="group-quote-card" className="iq-card p-6 md:p-8 mb-8 border-gold/30">
          <div className="flex items-start gap-3 mb-5">
            <Calculator className="h-6 w-6 text-gold mt-1 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="iq-overline">Indicative quote · live</div>
              <h2 className="font-display text-cream text-2xl md:text-3xl tracking-wider mt-1">Estimate before you ask.</h2>
              <p className="text-cream-dim text-sm mt-1">Adjust the sliders. Final invoice issued after our conductor confirms availability.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mb-5">
            <div>
              <label className="iq-label">Group size <span className="text-gold">{form.group_size}</span></label>
              <input data-testid="gq-size" type="range" min="5" max="200" step="1" value={form.group_size}
                     onChange={(e) => setForm({ ...form, group_size: Number(e.target.value) })}
                     className="w-full accent-gold cursor-pointer" />
              <div className="flex justify-between text-[10px] text-cream-dim/60 mt-1"><span>5</span><span>200</span></div>
            </div>
            <div>
              <label className="iq-label">Nights <span className="text-gold">{quoteNights}</span></label>
              <input data-testid="gq-nights" type="range" min="2" max="30" step="1" value={quoteNights}
                     onChange={(e) => setQuoteNights(Number(e.target.value))}
                     className="w-full accent-gold cursor-pointer" disabled={!!form.preferred_route_slug} />
              <div className="flex justify-between text-[10px] text-cream-dim/60 mt-1">
                <span>2</span>
                <span>{form.preferred_route_slug ? "set by route" : "30"}</span>
              </div>
            </div>
            <div>
              <label className="iq-label">Curated route</label>
              <select data-testid="gq-route" className="iq-input" value={form.preferred_route_slug || ""}
                      onChange={(e) => setForm({ ...form, preferred_route_slug: e.target.value || null })}>
                <option value="">Custom — use nights slider</option>
                {routes.map((r) => (
                  <option key={r.slug} value={r.slug}>{r.name} ({r.duration_days}d)</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border border-panel-border bg-ink rounded-sm p-4">
              <div className="iq-overline text-cream-dim/70 mb-1">Rail (bulk-discounted)</div>
              <div data-testid="gq-rail" className="font-display text-cream text-xl">{fmtEur(quote?.breakdown?.rail_total_eur)}</div>
              <div className="text-[11px] text-cream-dim/60">
                {quote ? `${fmtEur(quote.breakdown.rail_per_pax_eur)}/pax · -${quote.breakdown.rail_bulk_discount_pct}%` : "—"}
              </div>
            </div>
            <div className="border border-panel-border bg-ink rounded-sm p-4">
              <div className="iq-overline text-cream-dim/70 mb-1">Accommodation</div>
              <div data-testid="gq-stay" className="font-display text-cream text-xl">{fmtEur(quote?.breakdown?.stay_total_eur)}</div>
              <div className="text-[11px] text-cream-dim/60">
                {quote ? `${fmtEur(quote.breakdown.nightly_rate_eur)}/night/pax × ${quote.inputs.nights}n` : "—"}
              </div>
            </div>
            <div className="border border-panel-border bg-ink rounded-sm p-4">
              <div className="iq-overline text-cream-dim/70 mb-1">Per-person total</div>
              <div data-testid="gq-per-person" className="font-display text-gold text-2xl">{fmtEur(quote?.per_person_eur)}</div>
              <div className="text-[11px] text-cream-dim/60">incl. {quote?.breakdown?.margin_pct ?? 12}% conductor fee</div>
            </div>
            <div className="border border-gold/40 bg-gold/5 rounded-sm p-4">
              <div className="iq-overline text-gold mb-1">Group total</div>
              <div data-testid="gq-total" className="font-display text-gold text-3xl">{fmtEur(quote?.total_eur)}</div>
              <div className="text-[11px] text-cream-dim/60">
                {quoteLoading ? "computing…" : (quote?.indicative ? "Indicative — not binding" : "—")}
              </div>
            </div>
          </div>

          {selectedRouteName && (
            <p className="text-[11px] text-cream-dim/60 italic mt-3">
              Quote uses bulk rail for <span className="text-cream">{selectedRouteName}</span>. Switch to "Custom" to free the nights slider.
            </p>
          )}
        </div>

        <form data-testid="group-form" onSubmit={submit} className="iq-card p-6 md:p-8 space-y-5">
          <Honeypot value={website} onChange={setWebsite} name="website" />
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="iq-label">Organization *</label>
              <input data-testid="g-org" required maxLength={200} className="iq-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="St. Mary's Grammar School" />
            </div>
            <div>
              <label className="iq-label">Contact name *</label>
              <input data-testid="g-name" required maxLength={120} className="iq-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Ms. Davies" />
            </div>
            <div>
              <label className="iq-label">Email *</label>
              <input data-testid="g-email" required type="email" className="iq-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="trips@school.edu" />
            </div>
            <div>
              <label className="iq-label">Phone</label>
              <input data-testid="g-phone" className="iq-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+44 …" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <label className="iq-label">Group size *</label>
              <input data-testid="g-size" required type="number" min="5" max="500" className="iq-input" value={form.group_size} onChange={(e) => setForm({ ...form, group_size: Number(e.target.value) })} />
            </div>
            <div>
              <label className="iq-label">Age range *</label>
              <input data-testid="g-age" required className="iq-input" value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })} placeholder="15-17" />
            </div>
            <div>
              <label className="iq-label">Travel window *</label>
              <input data-testid="g-window" required className="iq-input" value={form.travel_window} onChange={(e) => setForm({ ...form, travel_window: e.target.value })} placeholder="July 2026, 10 days" />
            </div>
          </div>

          <div>
            <label className="iq-label">Preferred curated route (optional)</label>
            <select data-testid="g-route" className="iq-input" value={form.preferred_route_slug || ""} onChange={(e) => setForm({ ...form, preferred_route_slug: e.target.value || null })}>
              <option value="">No preference — build us a custom route</option>
              {routes.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.name} — {r.duration_days} days
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="iq-label">Cities of interest (optional)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {cities.map((c) => (
                <button
                  type="button"
                  key={c.slug}
                  data-testid={`g-city-${c.slug}`}
                  onClick={() => toggleCity(c.slug)}
                  className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                    form.cities.includes(c.slug) ? "bg-gold text-ink border-gold font-display tracking-wider" : "border-panel-border text-cream-dim hover:border-gold/40"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="iq-label">Budget per person (€)</label>
              <input data-testid="g-budget" type="number" min="0" className="iq-input" value={form.budget_per_person_eur} onChange={(e) => setForm({ ...form, budget_per_person_eur: e.target.value })} />
            </div>
            <div>
              <label className="iq-label">Special requirements</label>
              <input data-testid="g-special" className="iq-input" value={form.special_requirements} onChange={(e) => setForm({ ...form, special_requirements: e.target.value })} placeholder="Accessibility, dietary, supervised minors…" />
            </div>
          </div>

          {err && (
            <div data-testid="g-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-4 py-3 rounded-sm text-sm">{err}</div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[11px] text-cream-dim/60 italic">We respond within one business day. No spam. Quotes are non-binding.</p>
            <button data-testid="g-submit" type="submit" disabled={loading} className="iq-btn-primary disabled:opacity-50">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Submit inquiry <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
